import React, { useContext, useEffect, useState } from 'react';
import { Divider, Grid, Paper, useMediaQuery, useTheme } from '@material-ui/core';
import { OrderDetailContext } from 'compositions/OrderDetail/OrderDetailContext';
import { SelectedServiceType } from 'compositions/Wizard/Confirmation/WzardPartsAndServices';
import {
  GroupedInstallations,
  SelectedMountingOptionType,
} from 'compositions/Wizard/SelectTiresAndParts/MountingOption/GroupedInstallations';
import { useCurrentLocationIsDealer } from 'hooks/useCurrentLocationIsDealer';
import { OrderType } from 'types/OrderType';
import { AdditionalServicesType, Part } from 'types/UFOTypes';
import { INSTALLATION_TPMS } from 'utils/constantValues';
import { getCommonOrderPricesParams } from 'utils/getPricesCallCommonParams';
import { BREAKPOINT } from 'utils/responsivenessUtils';
import InstallationsList from './InstallationsList';
import PartInformation from './PartInformation';
import ProductSubTotal from './ProductSubTotal';
import ServiceInformation from './ServiceInformation';

interface EditablePartsProps {
  order: OrderType;
  part?: Part;
  service?: Part;
  setParts: (parts: Part[]) => void;
  printMode?: boolean;
  setSelected?: (part: Part) => void;
  selected: boolean;
  isEditing: boolean;
  disabled?: boolean;
  tiresCount: number;
  installations: Part[];
  smallSize?: boolean;
  options?: SelectedMountingOptionType[];
  additionalServices: AdditionalServicesType[];
}

export default function EditableParts(props: EditablePartsProps) {
  const {
    order,
    printMode = false,
    part,
    service,
    setSelected,
    selected,
    isEditing,
    setParts,
    disabled,
    installations,
    tiresCount,
    smallSize,
    options,
    additionalServices,
  } = props;
  const { setMountingOptionsModalOpen } = useContext(OrderDetailContext);

  const isDealer = useCurrentLocationIsDealer();
  const partQty: number = service ? Number(service.quantity) : Number(part?.quantity);
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down(BREAKPOINT));
  const [qty, setQty] = useState<number>(partQty);

  const isInstallation = !!service?.related_to;

  const partInstallations = part ? installations.filter((i) => i.related_to === part.related_to) : undefined;
  let noAddedInstallations = part
    ? additionalServices.filter(
        (i) =>
          part.ufo_details[0].service_mspn.includes(i.mspn) && !partInstallations?.some((pi) => pi.mspn === i.mspn),
      )
    : undefined;

  // Sort TPMS at first
  const tpms = noAddedInstallations?.filter((i) => i.mspn === INSTALLATION_TPMS);
  if (tpms) {
    noAddedInstallations = noAddedInstallations?.filter((i) => i.mspn !== INSTALLATION_TPMS);
    noAddedInstallations?.unshift(...tpms);
  }

  const allInstallations: any[] =
    partInstallations?.map((pi) => ({
      id: pi.id,
      part_id: pi.mspn,
      quantity: pi.quantity,
      part_description: pi.part_description || '',
      ispql: false,
      related_to: pi.related_to,
      unselectedInstallation: pi.unselectedInstallation,
      selected: pi.selected,
    })) || [];
  allInstallations.push(
    ...(noAddedInstallations?.map((i) => ({
      part_id: i.mspn,
      quantity: 1,
      part_description: i.description || '',
      ispql: false,
      related_to: part?.related_to,
      selected: false,
      noAddedInstallation: true,
    })) || []),
  );

  const stockingOrder = !order.vehicle.id;

  useEffect(() => {
    if (service && partQty !== qty) {
      setQty(partQty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  useEffect(() => {
    if (part && partQty !== qty) {
      setQty(partQty);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [part]);

  useEffect(() => {
    if (service && partQty !== qty) {
      setParts([
        {
          ...service,

          final_invoice_price: (Number(service.invoice_price) * qty).toFixed(2),
          quantity: qty,
        },
      ]);
    }
    if (part?.mspn && partQty !== qty) {
      const newParts = [
        {
          ...part,

          final_invoice_price: (Number(part.invoice_price) * qty).toFixed(2),
          quantity: qty,
        },
      ];
      // change qty of related installations
      if (partInstallations?.length) newParts.push(...partInstallations.map((i) => ({ ...i, quantity: qty })));
      setParts(newParts);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qty]);
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {/* NON-STOCKING ORDER */}
        {!stockingOrder && (
          <>
            {part && (
              <>
                <PartInformation
                  setMountingOptionsModalOpen={(part) => setMountingOptionsModalOpen(part)}
                  mobileView={mobileView}
                  status={order.status.primary}
                  qty={qty}
                  setQty={(q) => {
                    setQty(q);
                  }}
                  part={part.ufo_details[0]}
                  printMode={printMode}
                  isEditing={isEditing}
                  orderPrice={order.parts.find((op) => op.mspn === part.mspn) || undefined}
                  commonPricesParams={getCommonOrderPricesParams(order, isDealer)}
                  selected={selected}
                  setSelected={() => {
                    if (setSelected) setSelected(part);
                  }}
                  tiresCount={tiresCount}
                  stockingOrder={false}
                  smallSize={smallSize}
                />
                <InstallationsList
                  maxQty={qty} // qty of tires is max installations QTY, you shouldn't be able to install more tires than the ones you purchase
                  status={order.status.primary}
                  printMode={printMode}
                  setSelected={setSelected}
                  isEditing={isEditing}
                  disabled={disabled}
                  installations={allInstallations}
                  setQty={(q, editedService: SelectedServiceType) => {
                    const installation = partInstallations?.find(
                      (i) => i.mspn === editedService.part_id && i.related_to === editedService.related_to,
                    );
                    if (installation) {
                      setParts([
                        {
                          ...installation,
                          quantity: q,
                        },
                      ]);
                    }
                  }}
                  commonPricesParams={getCommonOrderPricesParams(order, isDealer)}
                  smallSize={smallSize}
                />
                {!isDealer && (
                  <ProductSubTotal
                    parts={order.parts.filter((p) => p.related_to === part.related_to)}
                    commonPricesParams={getCommonOrderPricesParams(order, isDealer)}
                    qty={qty}
                  />
                )}
                <Divider
                  style={{
                    width: '100%',
                    marginTop: 8,
                    marginBottom: 16,
                  }}
                />
              </>
            )}
            {service && !isInstallation && (
              <ServiceInformation
                mobileView={mobileView}
                disabled={disabled}
                qty={qty}
                setQty={(q) => {
                  setQty(q);
                }}
                status={order.status.primary}
                selected={selected}
                setSelected={() => {
                  if (setSelected) setSelected(service);
                }}
                service={service}
                printMode={printMode}
                isEditing={isEditing}
                orderPrice={service.final_invoice_price !== undefined ? service : undefined}
                commonPricesParams={getCommonOrderPricesParams(order, isDealer)}
                is_pql={service.pql_pricing || false}
                setPqlByMspn={(mspn, price) => {
                  setParts([
                    {
                      ...service,

                      invoice_price: price.toString(),
                      final_invoice_price: (price * service.quantity).toString(),
                      manual_pricing: true,
                    },
                  ]);
                }}
                smallSize={smallSize}
              />
            )}
          </>
        )}
        {/* STOCKING ORDER */}
        {stockingOrder && part && options && (
          <>
            <Paper elevation={2} style={{ marginBottom: 16, padding: 8 }}>
              <PartInformation
                removeTire={() => {
                  if (setSelected) setSelected(part);
                }}
                setMountingOptionsModalOpen={(part) => setMountingOptionsModalOpen(part)}
                withEditButton
                mobileView={mobileView}
                status={order.status.primary}
                qty={qty}
                setQty={(q) => {
                  setQty(q);
                }}
                part={part.ufo_details[0]}
                printMode={printMode}
                isEditing={isEditing}
                orderPrice={order.parts.find((op) => op.mspn === part.mspn) || undefined}
                commonPricesParams={getCommonOrderPricesParams(order, isDealer)}
                selected={selected}
                setSelected={() => {
                  if (setSelected) setSelected(part);
                }}
                tiresCount={tiresCount}
                stockingOrder
                smallSize={smallSize}
              />
              <Grid spacing={2} container style={{ justifyContent: 'flex-end' }}>
                <GroupedInstallations
                  order={order}
                  tire={part.ufo_details[0]}
                  relatedToCode={part.related_to}
                  installationsOptions={options}
                  commonPricesParams={getCommonOrderPricesParams(order, isDealer)}
                  singleColumn={smallSize}
                  isMobile={order.additional_information.appointment_type === 'is_mobile'}
                />
              </Grid>
            </Paper>
          </>
        )}
        {stockingOrder && service && !isInstallation && (
          <ServiceInformation
            mobileView={mobileView}
            disabled={disabled}
            qty={qty}
            setQty={(q) => {
              setQty(q);
            }}
            status={order.status.primary}
            selected={selected}
            setSelected={() => {
              if (setSelected) setSelected(service);
            }}
            service={service}
            printMode={printMode}
            isEditing={isEditing}
            orderPrice={service.final_invoice_price !== undefined ? service : undefined}
            commonPricesParams={getCommonOrderPricesParams(order, isDealer)}
            is_pql={service.pql_pricing || false}
            setPqlByMspn={(mspn, price) => {
              setParts([
                {
                  ...service,
                  invoice_price: price.toString(),
                  final_invoice_price: (price * service.quantity).toString(),
                  manual_pricing: true,
                },
              ]);
            }}
            smallSize={smallSize}
          />
        )}
      </Grid>
    </Grid>
  );
}
